import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MAX_LIVE_BUFFER_MS } from "@/machines/utils/live-playback-buffer";

/**
 * How far behind the newest footage live playback deliberately sits.
 *
 * This is a trade the operator is better placed to make than we are, because it
 * depends on their site and their job. Sitting further back plays continuously;
 * sitting closer shows the most recent thing that exists but starves, because
 * footage only becomes fetchable 17–68s after a clip ENDS (measured across four
 * cameras) — a player closer to the edge than that runs out of material and
 * freezes.
 *
 * The floor here is the FLOOR of an adaptive buffer, not a fixed lag:
 * `liveBufferMs` still widens it when a camera's own arrivals have been slower,
 * and `MAX_LIVE_BUFFER_MS` still caps it. Lowering this does not pin playback
 * closer than the pipeline can sustain; it only stops the client padding beyond
 * what that camera has needed lately.
 *
 * Note the total lag an operator perceives is larger than this value: the newest
 * clip is itself ~1 minute old by the time it can be fetched, so a 2-minute
 * buffer reads as roughly 3 minutes behind the wall clock. The choices below are
 * expressed as the buffer, and the panel states the practical consequence.
 */

/** Selectable buffers, in ms. Bounded by what the buffer maths already allows. */
export const LIVE_DELAY_CHOICES = [60_000, 120_000, 180_000] as const;
export type LiveDelayMs = (typeof LIVE_DELAY_CHOICES)[number];

/** Matches `MIN_LIVE_BUFFER_MS` — the measured stall-free floor. */
export const DEFAULT_LIVE_DELAY_MS: LiveDelayMs = 120_000;

interface LiveDelayState {
  /** Floor for the adaptive live buffer, in ms. */
  liveDelayMs: LiveDelayMs;
  setLiveDelayMs: (ms: LiveDelayMs) => void;
}

export const useLiveDelayStore = create<LiveDelayState>()(
  persist(
    (set) => ({
      liveDelayMs: DEFAULT_LIVE_DELAY_MS,
      setLiveDelayMs: (ms) =>
        set({
          // Clamped to the range the buffer maths is defined over, so a stale
          // persisted value from an older build can never park playback outside
          // it. The lower bound is deliberately below the measured stall-free
          // floor: an operator may choose to sit closer and accept the stalls.
          liveDelayMs: Math.min(MAX_LIVE_BUFFER_MS, Math.max(30_000, ms)) as LiveDelayMs,
        }),
    }),
    { name: "maestro-live-delay" },
  ),
);
